import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import { PersonSearch, Suggestion } from "../../pages/LiPeople/schema/liPeople";
import { AiColumn } from "../../pages/List/schema/ai-column";
import { ColumnConfig, ColumnConfigSchema } from "../../pages/List/schema/columnConfig";
import { ColumnValue } from "../../pages/List/schema/columnValue";
import { UpdateList } from "../../pages/List/schema/list";
import { TableConfig, TableConfigSchema } from "../../pages/List/schema/tableConfig";
import { GoogleNewsResponse } from "../../pages/ProfileTracker/schema/google-news.interface";
import { LiCompanyActivitiesInterface } from "../../pages/ProfileTracker/schema/li-company-activity-interface";
import { ManualCompany } from "../../pages/ProfileTracker/schema/manual-company.interface";
import {
	CreateTracker,
	Tracker,
	TrackerFile,
	TrackerFileSchema,
	TrackerRequestPaginationParams,
	TrackerResponse,
	TrackerResponseSchema,
	TrackerSchema,
} from "../../pages/ProfileTracker/schema/profileTracker";
import { CreateTrackerColumn, CustomColumn } from "../../pages/ProfileTracker/schema/trackerColumn";
import {
	AddPeopleToTrackerResponse,
	AddPeopleToTrackerResponseSchema,
	TrackerOrganization,
	TrackerOrganizationResponse,
	TrackerOrganizationResponseSchema,
	TrackerOrganizationSchema,
} from "../../pages/ProfileTracker/schema/trackerOrganizations";
import {
	removeTrackerPersonFromRecord,
	updateTrackerPersonsInRecord,
} from "./profileTracker.slice";

export const profileTrackerApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProfileTrackers: builder.query<TrackerResponse, RequestPaginationParams>({
			query: ({ sorting, ...params }) => ({
				url: "tracker",
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: TrackerResponseSchema,
			}),
			providesTags: ["ProfileTracker"],
		}),
		getTrackerTableConfig: builder.query<TableConfig, number>({
			query: (id: number) => ({
				url: `tracker/config/${id}`,
				responseSchema: TableConfigSchema,
			}),
			providesTags: ["TrackerTableConfig"],
		}),
		createTrackerTableConfig: builder.mutation<Partial<TableConfig>, Partial<TableConfig>>({
			query: (body) => ({
				url: `tracker/config`,
				method: "POST",
				body,
				responseSchema: TableConfigSchema,
			}),
			invalidatesTags: ["TrackerTableConfig"],
		}),
		addOrganizationToTracker: builder.mutation<
			Partial<Tracker>,
			{ companies: { id: string }[]; id: number }
		>({
			query: ({ id, companies }) => ({
				url: `tracker/add-organization-by-enrich-id/${id}`,
				method: "POST",
				body: {
					enrichIds: companies.map((c) => c.id),
				},
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		addManualOrganizationToTracker: builder.mutation<
			Partial<Tracker>,
			ManualCompany & { id: number }
		>({
			query: ({ id, name, websiteUrl, linkedinUrl }) => ({
				url: `tracker/add-manual-organization/${id}`,
				method: "POST",
				body: {
					name,
					websiteUrl,
					linkedinUrl,
				},
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		addHoldingToOrganization: builder.mutation<
			Partial<Tracker>,
			{ company: Suggestion; id: number }
		>({
			query: ({ id, company }) => ({
				url: `tracker-organization/add-holding/${id}`,
				method: "POST",
				body: {
					urn: +company.urn,
				},
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		removeHoldingToOrganization: builder.mutation<Partial<Tracker>, { id: number }>({
			query: ({ id }) => ({
				url: `tracker-organization/remove-holding/${id}`,
				method: "DELETE",
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		addPeopleToTrackerOrganization: builder.mutation<
			Partial<TrackerOrganization>,
			{ people: PersonSearch[]; id: number }
		>({
			query: ({ id, people }) => ({
				url: `tracker-organization/${id}`,
				method: "POST",
				body: {
					linkedinUrls: people.map((person) => person.navigationUrl),
				},
				responseSchema: TrackerOrganizationSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		// Adds people to the tracker that backs a TRACKER-source list, by LinkedIn URL — creating the
		// tracker organization + person and scheduling signal scoring (unlike the plain list add,
		// which only creates a list_person and never gets the lead a signal).
		addPeopleToTrackerByLinkedinUrl: builder.mutation<
			AddPeopleToTrackerResponse,
			{ listId: number; linkedinUrls: string[] }
		>({
			query: ({ listId, linkedinUrls }) => ({
				url: `tracker/list/${listId}/add-people-by-linkedin-url`,
				method: "POST",
				body: { linkedinUrls },
				responseSchema: AddPeopleToTrackerResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"ListPerson",
				{ type: "ProfileTracker", id: arg.listId },
			],
		}),
		removePersonFromTrackerOrganization: builder.mutation<
			Partial<TrackerOrganization>,
			{ personId: number; id: number }
		>({
			query: ({ id, personId }) => ({
				url: `tracker-organization/${id}/${personId}`,
				method: "DELETE",
				responseSchema: TrackerOrganizationSchema,
			}),
			async onQueryStarted({ id, personId }, { dispatch, queryFulfilled }) {
				// Optimistic update: remove the person from the cache immediately
				const patchResult = dispatch(
					profileTrackerApi.util.updateQueryData("getTrackerOrganization", id, (draft) => {
						if (draft.trackerPersons) {
							const personIndex = draft.trackerPersons.findIndex((p) => p.id === personId);

							if (personIndex !== -1) {
								draft.trackerPersons.splice(personIndex, 1);
							}
						}
					})
				);

				// Also update the Redux slice records array (for TrackerDetailsTable)
				dispatch(removeTrackerPersonFromRecord({ organizationId: id, personId }));
				try {
					await queryFulfilled;
				} catch {
					// Rollback on error
					patchResult.undo();
					// Note: We would need to restore the slice too, but since we're optimistic
					// and errors are rare, a page refresh will fix this edge case
				}
			},
		}),
		addProspectToTrackerOrganization: builder.mutation<
			Partial<TrackerOrganization>,
			{ prospectId: number; id: number }
		>({
			query: ({ id, prospectId }) => ({
				url: `tracker-organization/${id}/prospect`,
				method: "POST",
				body: { prospectId },
				responseSchema: TrackerOrganizationSchema,
			}),
			async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
				// Wait for the API response to get the new trackerPerson data
				try {
					const { data: updatedOrganization } = await queryFulfilled;

					// Update the cache with the response data
					dispatch(
						profileTrackerApi.util.updateQueryData("getTrackerOrganization", id, (draft) => {
							if (updatedOrganization.trackerPersons) {
								draft.trackerPersons = updatedOrganization.trackerPersons;
							}
						})
					);
					// Also update the Redux slice records array (for TrackerDetailsTable)
					if (updatedOrganization.trackerPersons) {
						dispatch(
							updateTrackerPersonsInRecord({
								organizationId: id,
								trackerPersons: updatedOrganization.trackerPersons,
							})
						);
					}
				} catch {
					// Error handling - no rollback needed since we didn't do optimistic update
				}
			},
		}),
		updateTrackerTableConfig: builder.mutation<Partial<TableConfig>, Partial<TableConfig>>({
			query: ({ id, ...body }) => ({
				url: `tracker/config/${id}`,
				method: "PATCH",
				body,
				responseSchema: TableConfigSchema,
			}),
			invalidatesTags: ["TrackerTableConfig"],
		}),
		getTracker: builder.query<Tracker, number>({
			query: (id) => ({
				url: `tracker/${id}`,
				responseSchema: TrackerSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "ProfileTracker", id: result?.id || arg }],
		}),
		getTrackerOrganizations: builder.query<
			TrackerOrganizationResponse,
			TrackerRequestPaginationParams
		>({
			query: ({ sorting, filter, listId, signalFilterStatus, ...params }) => ({
				url: `tracker-organization/${listId}`,
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
					...(filter && { filter: JSON.stringify(filter) }),
					...(signalFilterStatus && { signalFilterStatus: JSON.stringify(signalFilterStatus) }),
				},
				responseSchema: TrackerOrganizationResponseSchema,
			}),
			providesTags: ["TrackerOrganization"],
		}),
		getTrackerOrganization: builder.query<TrackerOrganization, number>({
			query: (id) => ({
				url: `tracker-organization/${id}/details`,
				responseSchema: TrackerOrganizationSchema,
			}),
			providesTags: (result, error, arg) => [
				{ type: "TrackerOrganization", id: result?.id || arg },
			],
		}),
		createTracker: builder.mutation<Partial<Tracker>, CreateTracker>({
			query: (body) => ({
				url: `tracker`,
				method: "POST",
				body,
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: ["ProfileTracker"],
		}),
		createTrackerFile: builder.mutation<Partial<TrackerFile>, FormData>({
			query: (body) => ({
				url: `tracker-file`,
				method: "POST",
				body,
				responseSchema: TrackerFileSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.get("trackerId")) {
					return [
						{ type: "ProfileTracker", id: +(arg.get("trackerId") || 0) },
						"ProfileTrackerFile",
					];
				}

				if (arg.get("listId")) {
					return [{ type: "List", id: +(arg.get("listId") || 0) }, "ProfileTrackerFile"];
				}

				return ["ProfileTrackerFile"];
			},
		}),
		downloadTrackerFile: builder.query<Blob, number>({
			query: (trackerFileId) => ({
				url: `tracker-file/${trackerFileId}`,
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		updateTrackerFile: builder.mutation<Partial<TrackerFile>, Partial<TrackerFile>>({
			query: ({ id, ...body }) => ({
				url: `tracker-file/${id}`,
				method: "PATCH",
				body,
				responseSchema: TrackerFileSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.trackerId) {
					return [{ type: "ProfileTracker", id: +(arg.trackerId || 0) }, "ProfileTrackerFile"];
				}

				return ["ProfileTrackerFile"];
			},
		}),
		updateTracker: builder.mutation<Partial<Tracker>, Partial<Tracker>>({
			query: ({ id, ...body }) => ({
				url: `tracker/${id}`,
				method: "PATCH",
				body,
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.id) {
					return [{ type: "ProfileTracker", id: +(arg.id || 0) }];
				}

				return ["ProfileTracker"];
			},
		}),

		deleteTrackerFile: builder.mutation<Partial<TrackerFile>, number>({
			query: (id) => ({
				url: `tracker-file/${id}`,
				method: "DELETE",
				responseSchema: TrackerFileSchema,
			}),
			invalidatesTags: ["ProfileTrackerFile"],
		}),
		deleteTracker: builder.mutation<number, number>({
			query: (id) => ({
				url: `tracker/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["ProfileTracker"],
		}),
		addColumnToTracker: builder.mutation<Partial<Tracker>, CreateTrackerColumn>({
			query: ({ id, ...body }) => ({
				url: `tracker-column/${id}`,
				method: "POST",
				body,
				responseSchema: TrackerSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"TrackerOrganization",
				"Column",
				{ type: "ProfileTracker", id: arg.id },
			],
		}),
		deleteColumn: builder.mutation<number, Partial<UpdateList>>({
			query: ({ id }) => ({
				url: `tracker-column/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (result) => {
				return ["TrackerOrganization", "Column", { type: "ProfileTracker", id: result }];
			},
		}),
		getTrackerColumnById: builder.query<ColumnConfig, number>({
			query: (id) => ({
				url: `tracker-column/${id}`,
				responseSchema: ColumnConfigSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "Column", id: arg }],
		}),
		updateColumnValue: builder.mutation<{ id: number; columnValue: ColumnValue }, CustomColumn>({
			query: ({ id, ...body }) => ({
				url: `tracker-column/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		runAiPrompt: builder.mutation<
			{
				data: {
					answer: string;
					sourceUrl: string;
				};
			},
			AiColumn
		>({
			query: ({ id, ...body }) => ({
				url: `tracker-column/run/${id}`,
				method: "POST",
				body,
			}),
		}),
		runSearch: builder.mutation<{ data: GoogleNewsResponse }, AiColumn>({
			query: ({ id, ...body }) => ({
				url: `tracker-column/search/${id}`,
				method: "POST",
				body,
			}),
		}),
		runLinkedinPostSearch: builder.mutation<{ data: LiCompanyActivitiesInterface }, AiColumn>({
			query: ({ id, ...body }) => ({
				url: `tracker-column/linkedin-post-search/${id}`,
				method: "POST",
				body,
			}),
		}),
		deleteTrackerOrganizations: builder.mutation<void, { ids: number[] }>({
			query: ({ ids }) => ({
				url: `tracker/organization/${ids.join(",")}`,
				method: "DELETE",
			}),
			invalidatesTags: ["TrackerOrganization"],
		}),
		downloadTrackers: builder.mutation<Blob, string>({
			query: (ids) => ({
				url: `tracker/download-bulk?ids=${ids}`,
				responseHandler: async (response) => response.blob(),
			}),
		}),
	}),
});

export const {
	useGetProfileTrackersQuery,
	useLazyGetProfileTrackersQuery,
	useGetTrackerQuery,
	useLazyGetTrackerQuery,
	useCreateTrackerMutation,
	useCreateTrackerFileMutation,
	useDeleteTrackerFileMutation,
	useUpdateTrackerFileMutation,
	useUpdateTrackerMutation,
	useDeleteTrackerMutation,
	useGetTrackerOrganizationsQuery,
	useLazyGetTrackerOrganizationsQuery,
	useGetTrackerTableConfigQuery,
	useCreateTrackerTableConfigMutation,
	useUpdateTrackerTableConfigMutation,
	useAddColumnToTrackerMutation,
	useDeleteColumnMutation,
	useUpdateColumnValueMutation,
	useRunAiPromptMutation,
	useRunSearchMutation,
	useRunLinkedinPostSearchMutation,
	useGetTrackerColumnByIdQuery,
	useAddPeopleToTrackerOrganizationMutation,
	useAddPeopleToTrackerByLinkedinUrlMutation,
	useRemovePersonFromTrackerOrganizationMutation,
	useAddProspectToTrackerOrganizationMutation,
	useAddOrganizationToTrackerMutation,
	useAddManualOrganizationToTrackerMutation,
	useAddHoldingToOrganizationMutation,
	useRemoveHoldingToOrganizationMutation,
	useLazyDownloadTrackerFileQuery,
	useDeleteTrackerOrganizationsMutation,
	useDownloadTrackersMutation,
	useGetTrackerOrganizationQuery,
} = profileTrackerApi;
export const accountApiReducer = profileTrackerApi.reducer;
export const accountApiMiddleware = profileTrackerApi.middleware;
